<template>
  <BasicForm
    name="table-batch-bar"
    class="!px-8px !py-0"
    v-bind="getFormProps"
    :tableAction="table"
    @register="registerForm"
    @field-value-change="fieldValueChange"
  >
    <template v-for="item in getBatchSlotKeys" #[replaceBatchSlotKey(item)]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <template #formFooter>
      <MButton v-bind="batchButtonOptions" :disabled="disabled" @click="handleBatch">
        {{ t(batchButtonOptions?.text || 'c.25090339') }}
      </MButton>
      <slot name="formFooter"></slot>
    </template>
  </BasicForm>
</template>

<script lang="ts" setup>
  import { computed, ref, useSlots } from 'vue';
  import { isNil, message, omit } from '@mts-fe/utils';
  import { MButton } from '@mts/components/Button';
  import { BasicForm, FormProps, FormSchema, useForm } from '@mts/components/Form';
  import { t } from '@mts/hooks/web/useI18n';
  import { useTableContext } from '../hooks/useTableContext';
  import type { BatchBarConfig } from '../types/table';

  defineOptions({
    name: 'TableBatchBar',
  });

  const table = useTableContext();
  const [registerForm, formAction] = useForm();

  const props = withDefaults(defineProps<BatchBarConfig>(), {
    checkSelect: true,
  });

  const getFormProps = computed((): Partial<FormProps> => {
    const schemas = props.schemas?.map(({ colProps, ...i }) => i) as FormProps['schemas'];
    return {
      ...omit(props, ['labelWidth', 'baseColProps', 'rowProps']),
      layout: 'inline',
      schemas,
    };
  });

  const slots = useSlots();
  const getBatchSlotKeys = computed(
    () =>
      Object.keys(slots)
        .map((item) => (item.startsWith('batch-') ? item : null))
        .filter((item) => !!item) as string[],
  );
  const replaceBatchSlotKey = (key: string) => (key ? (key.replace?.(/batch-/, '') ?? '') : '');

  const disabled = ref(true);
  const fieldValueChange = (_key: string, _value: any, _schema: FormSchema) => {
    disabled.value = !Object.values({ ...formAction.getFieldsValue() }).filter(
      (i) => ![null, '', void 0].includes(i),
    ).length;
  };

  const handleBatch = () => {
    if (props.checkSelect && !table.getSelectRowKeys().length) {
      message.warn(t('component.table.selectionBarEmpty'));
      return false;
    }
    if (props.onBatch) {
      props.onBatch();
    } else {
      const batchInfo = formAction.getFieldsValue();
      const _updateRecord = (record: Recordable, batchInfo: Recordable) =>
        Object.entries(batchInfo).forEach(([key, value]) => !isNil(value) && (record[key] = value));

      const { rowKey } = table.getBindValues.value;
      const dataSource = table.getSelectRows().length
        ? table.getSelectRows()
        : table.getDataSource();
      dataSource.forEach((record) => {
        (props?.updateRecord || _updateRecord)(record, batchInfo);
        table.updateTableDataRecord(record[rowKey as string], { ...record }, true);
      });
      table.clearSelectedRowKeys();
      formAction.resetFields();
      disabled.value = true;
    }
  };
</script>
